import ninegrid from "../index.js";

export class nxDiv extends HTMLElement {
	originContents;
	#isInitialized = false;
	#root; // Shadow DOM 사용 여부에 따라 shadowRoot 또는 this를 가리킴
	#hasChanged = false;

	constructor() {
		super();
		// constructor에서는 속성을 읽을 수 없으므로 초기화만 준비합니다.
	}

	connectedCallback() {
		if (!this.#isInitialized) {
			// 1. 여기서 속성을 읽어 Shadow DOM 사용 여부를 결정합니다.
			// use-shadow 속성이 "false"가 아닐 때만 Shadow DOM을 생성 (기본값 true)
			const useShadow = this.getAttribute("use-shadow") !== "false";

			if (useShadow && !this.shadowRoot) {
				this.attachShadow({ mode: 'open' });
				this.#root = this.shadowRoot;
			} else {
				this.#root = this;
			}

			this.#init();
			this.#isInitialized = true;
			return true;
		}
		return false;
	}

	get changed() {
		return this.#hasChanged;
	}

	set changed(v) {
		const value = Boolean(v);
		this.#hasChanged = value;

		// 기존 #changed 로직을 setter 내부로 통합
		const parent = this.closest(".detail-wrapper");
		if (parent) {
			const el = parent.querySelector("nx-title2");
			if (el) el.changed = value;
		}
	}

	// 자식 클래스에서 접근 가능하도록 getter 제공
	get root() {
		return this.#root || this;
	}

	// 공통 쿼리 함수: 현재 root(Shadow 또는 Light DOM)에서 요소를 찾음
	#getElements() {
		// ShadowRoot가 있으면 shadowRoot에서, 없으면 본체(this)에서 찾음
		return ninegrid.querySelectorAll("input[name], textarea[name], select[name], nx-editor[name]", this.#root);
	}

	// [공통 로직 1] 특정 요소에 값을 쓰거나 초기화하는 핵심 함수
	#updateElement(el, value) {
		const tagName = el.tagName.toUpperCase();
		const type = el.type;
		let isChanged = false;

		if (["INPUT", "TEXTAREA", "SELECT", "NX-EDITOR"].includes(tagName)) {
			if (type === "checkbox") {
				const t = el.getAttribute("true-value") || "Y";
				const isChecked = (String(t) === String(value));
				if (el.checked !== isChecked) isChanged = true;
				el.checked = isChecked;
			} else if (type === "radio") {
				const isChecked = (String(el.value) === String(value));
				if (el.checked !== isChecked) isChanged = true;
				el.checked = isChecked;
			} else {
				if (el.value !== value) isChanged = true;
				el.value = value;
			}
		} else {
			if (el.textContent !== value) isChanged = true;
			el.textContent = value;
		}
		return isChanged;
	}

	// [공통 로직 2] 이벤트 리스너 일괄 등록
	#refreshListeners() {
		this.#getElements().forEach(el => {
			el.removeEventListener("input", this.#changeHandler);
			el.addEventListener("input", this.#changeHandler);
		});
	}

	getData = () => {
		const jsonData = {};
		this.#getElements().forEach(el => {
			const key = el.name;
			if (!key) return;

			let value;
			if (el.tagName === "INPUT" && el.type === "checkbox") {
				let t = el.getAttribute("true-value") || "Y";
				let f = el.getAttribute("false-value") || "N";
				value = el.checked ? t : f;
			} else if (el.tagName === "INPUT" && el.type === "radio") {
				value = el.value;
			} else {
				value = el.value;
			}

			if (jsonData[key]) {
				if (!Array.isArray(jsonData[key])) jsonData[key] = [jsonData[key]];
				jsonData[key].push(value);
			} else {
				jsonData[key] = value;
			}
		});
		return jsonData;
	};

	// 파라미터가 있으면 해당 값으로, 없으면 전체 공백 초기화
	clearData = (jsonData = {}) => {
		this.#refreshListeners();
		this.#getElements().forEach(el => {
			const key = el.name;
			if (!key) return;
			const val = (jsonData && jsonData[key] !== undefined) ? jsonData[key] : "";
			this.#updateElement(el, val);
		});
		this.changed = false;
	};

	setData = (jsonData) => {
		if (!jsonData || typeof jsonData !== 'object') return;
		this.#refreshListeners();

		let bChanged = false;
		this.#getElements().forEach(el => {
			const key = el.name;
			if (!key || !jsonData.hasOwnProperty(key)) return;

			if (this.#updateElement(el, jsonData[key])) {
				bChanged = true;
			}
		});
		if (bChanged) this.changed = true;
	};

	initData = (jsonData) => {
		this.clearData(jsonData);
		this.changed = false;
	};

	#changeHandler = (e) => {
		this.changed = true;
	}

	#init = () => {
		/** CSS style 적용 */
		for (const attr of this.attributes) {
			if (attr.name.startsWith("css-")) {
				this.style.setProperty(attr.name.substring(4), attr.value);
			}
		}

		//console.log("=========");
		//console.log(this.innerHTML.trim());
		this.originContents = this.innerHTML.trim();

		this.innerHTML = ""; // 기존 내부 HTML 제거

		// 만약 순수 nx-div로만 쓰일 경우(상속 x), 내용을 shadow에 채워줌
		if (this.shadowRoot && this.tagName.toLowerCase() === 'nx-div') {
			this.shadowRoot.innerHTML = this.originContents;
		}
	};
}